import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    fetchDeletedDocuments,
    fetchDocuments,
    fetchSharedDocuments,
    fetchStorageCounters,
    fetchStorageSize,
    foreverDeleteDocument,
    foreverDeleteTrashed,
    restoreDeletedDocument,
    softDeleteDocument,
    uploadDocument,
    downloadDocument,
    bulkDownload,
    fetchDocumentTree,
    fetchDocumentTreeChildren,
    fetchDocumentTreePath,
} from './documentsActions';
import { TreeDataNode } from 'antd';
import { BreadcrumbItemType, DocumentTeamType } from '../types';

type LoadedChildren = Map<number, TreeDataNode[]>;

// Indexed over the whole tree, not just the level being replaced, so a folder that
// moved in from elsewhere keeps the children it already had.
const indexLoadedChildren = (nodes: TreeDataNode[], into: LoadedChildren = new Map()): LoadedChildren => {
    nodes.forEach((node) => {
        if (!node.children?.length) return;

        into.set(Number(node.key), node.children);
        indexLoadedChildren(node.children, into);
    });
    return into;
};

// A re-fetched level arrives shallow. Re-attach the children each node already had:
// dropping them blanks out every expanded sub-branch, and rc-tree keeps those keys
// in loadedKeys, so it would not lazily fetch them a second time.
const withLoadedChildren = (incoming: TreeDataNode[], loaded: LoadedChildren): TreeDataNode[] =>
    incoming.map((node) => {
        // isLeaf means the folder has no sub-folders any more, so whatever we held
        // for it is stale — keep the fresh (empty) state instead.
        const children = node.isLeaf ? undefined : loaded.get(Number(node.key));
        return children?.length ? { ...node, children } : node;
    });

// Recursively merge a fresh level into the node it belongs to; false if not found.
const mergeNodeChildren = (nodes: TreeDataNode[], key: number, children: TreeDataNode[]): boolean => {
    const merge = (branch: TreeDataNode[], loaded: LoadedChildren): boolean => {
        for (const node of branch) {
            if (Number(node.key) === key) {
                node.children = withLoadedChildren(children, loaded);
                return true;
            }
            if (node.children && merge(node.children, loaded)) {
                return true;
            }
        }
        return false;
    };

    return merge(nodes, indexLoadedChildren(nodes));
};

const setNodeTitle = (nodes: TreeDataNode[], key: number, title: string): boolean => {
    for (const node of nodes) {
        if (Number(node.key) === key) {
            node.title = title;
            return true;
        }
        if (node.children && setNodeTitle(node.children, key, title)) {
            return true;
        }
    }
    return false;
};

interface GroupTeams {
    [key: string]: DocumentTeamType;
}

interface Filter {
    [key: string]: any;
    label: string;
    value: any;
}

export interface FiltersState {
    [key: string]: any;
    type?: Filter;
    created_by?: Filter;
    updated_by?: Filter;
    modified_before?: Filter;
    modified_after?: Filter;
    created_before?: Filter;
    created_after?: Filter;
    modified?: any;
    has_versions?: Filter;
    logic?: 'and' | 'or';
    in_descendants_of?: Filter;
}

interface ParamsState {
    [key: string]: any;
    query?: string | null;
    parent_id?: string | null;
    filters?: FiltersState | null;
}

interface DocumentsState {
    listDocuments: any[];
    breadcrumbs: BreadcrumbItemType[];
    shareables: any[];
    documentTree: TreeDataNode[];
    teams: GroupTeams;
    users: { [key: string]: any };
    storageCounters: { [key: string]: any };
    storageSize: { size: number; limit: number } | null;
    loading: boolean;
    loadingDocumentTree: boolean;
    error: any | null;
    params: ParamsState;
}

export const initialState: DocumentsState = {
    listDocuments: [],
    breadcrumbs: [],
    shareables: [],
    documentTree: [],
    teams: {},
    users: [],
    storageCounters: [],
    storageSize: null,
    loading: false,
    loadingDocumentTree: false,
    error: null,
    params: {},
};

const documentsSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        reset: (state) => {
            state.listDocuments = [];
            state.breadcrumbs = [];
            state.shareables = [];
            state.documentTree = [];
            state.teams = {};
            state.users = [];
            state.params = {};
        },
        setParams: (state, action: PayloadAction<ParamsState>) => {
            let preparams = JSON.parse(JSON.stringify(state.params));
            Object.keys(action.payload).forEach((key) => {
                preparams[key] = action.payload[key];
            });
            state.params = preparams;
        },
        deleteFilter: (state, action: PayloadAction<any>) => {
            let preparams = JSON.parse(JSON.stringify(state.params));
            if (action.payload.id && preparams.filters[action.payload.key]) {
                preparams.filters[action.payload.key].value.forEach((element: any, index: any) => {
                    if (element.id === action.payload.id) {
                        preparams.filters[action.payload.key].value.splice(index, 1);
                    }
                });
                if (preparams.filters[action.payload.key].value.length == 0) {
                    delete preparams.filters[action.payload.key];
                }
            } else {
                delete preparams.filters[action.payload.key];
                if (action.payload.key == 'modified_before' || action.payload.key == 'modified_after') {
                    delete preparams.filters['modified'];
                    delete preparams.filters['modified_before'];
                    delete preparams.filters['modified_after'];
                }
            }
            state.params = preparams;
        },
        clearParams: (state) => {
            state.params = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchStorageCounters.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchStorageCounters.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
            if (!action.payload) return;

            state.storageCounters = action.payload.data;
        });
        builder.addCase(fetchStorageCounters.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload?.data;
        });

        builder.addCase(fetchStorageSize.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchStorageSize.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
            if (!action.payload) return;

            state.storageSize = action.payload;
        });
        builder.addCase(fetchStorageSize.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload?.data;
        });

        builder.addCase(fetchDocuments.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchDocuments.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
            if (!action.payload) return;

            state.listDocuments = action.payload.data.list.map((item: any) => {
                item.key = item.id;
                return item;
            });
            state.teams = action.payload.data.teams;
            state.users = action.payload.data.users;
            state.shareables = action.payload.data.shareables;
            state.breadcrumbs = action.payload.data.breadcrumbs;
        });
        builder.addCase(fetchDocuments.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload?.data;
        });

        builder.addCase(fetchDocumentTree.pending, (state) => {
            state.loadingDocumentTree = true;
            state.error = null;
        });
        builder.addCase(fetchDocumentTree.fulfilled, (state, action: PayloadAction<any>) => {
            state.loadingDocumentTree = false;
            if (!action.payload) return;

            const tree = action.payload.data.documentTree ?? [];
            const [root] = tree;
            if (!root) return;

            // On a deep link this races fetchDocumentTreePath, which may already have
            // merged the ancestor levels in. Replacing the tree wholesale would throw
            // those away and leave the expanded path blank, so merge this (root-level
            // only) response into the root we already have.
            const merged =
                state.documentTree.length &&
                mergeNodeChildren(state.documentTree, Number(root.key), root.children ?? []);
            if (merged) return;

            state.documentTree = tree;
        });
        builder.addCase(fetchDocumentTree.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload?.data;
        });

        builder.addCase(fetchDocumentTreeChildren.fulfilled, (state, action: PayloadAction<any>) => {
            if (!action.payload) return;
            const { parentId, children, parentTitle } = action.payload;
            mergeNodeChildren(state.documentTree, Number(parentId), children);
            if (parentTitle) {
                setNodeTitle(state.documentTree, Number(parentId), parentTitle);
            }
        });

        builder.addCase(fetchDocumentTreePath.fulfilled, (state, action: PayloadAction<any>) => {
            if (!action.payload) return;
            const levels = action.payload.data?.levels ?? {};
            // Make sure the synthetic root exists before merging deeper levels into
            // it (this can resolve before the initial fetchDocumentTree call does).
            if (!state.documentTree.length) {
                state.documentTree = [{ title: 'Documents', key: 0, children: levels['0'] ?? [] }];
            }
            Object.keys(levels).forEach((key) => {
                mergeNodeChildren(state.documentTree, Number(key), levels[key]);
            });
        });

        builder.addCase(uploadDocument.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(uploadDocument.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(uploadDocument.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });
        builder.addCase(softDeleteDocument.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(softDeleteDocument.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(softDeleteDocument.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(fetchSharedDocuments.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchSharedDocuments.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;

            if (!action.payload) return;
            state.breadcrumbs = action.payload.data.breadcrumbs;
            state.listDocuments = action.payload.data.list.map((item: any) => {
                item.key = item.id;
                return item;
            });
            state.teams = action.payload.data.teams;
        });
        builder.addCase(fetchSharedDocuments.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(fetchDeletedDocuments.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchDeletedDocuments.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;

            if (!action.payload) return;
            state.breadcrumbs = action.payload.data.breadcrumbs;
            state.listDocuments = action.payload.data.list.map((item: any) => {
                item.key = item.id;
                return item;
            });
        });
        builder.addCase(fetchDeletedDocuments.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(foreverDeleteDocument.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(foreverDeleteDocument.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(foreverDeleteDocument.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(foreverDeleteTrashed.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(foreverDeleteTrashed.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(foreverDeleteTrashed.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(restoreDeletedDocument.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(restoreDeletedDocument.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(restoreDeletedDocument.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(downloadDocument.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(downloadDocument.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(downloadDocument.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });

        builder.addCase(bulkDownload.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(bulkDownload.fulfilled, (state, action: PayloadAction<any>) => {
            state.loading = false;
        });
        builder.addCase(bulkDownload.rejected, (state, action: PayloadAction<any>) => {
            state.loading = false;
            state.error = action.payload.data;
        });
    },
});

export const { reset, setParams, clearParams, deleteFilter, clearError} = documentsSlice.actions;
export default documentsSlice.reducer;
